import { readJson, writeJson } from './storage';
import { createStore, useStore } from './store';

export type SoundName = 'fire0' | 'fire1' | 'bounce' | 'fizzle' | 'explode' | 'tick' | 'go' | 'round' | 'match' | 'click';

/**
 * Tiny synthesized sound kit. The AudioContext is created only after the
 * first user gesture (browsers require it, and we never want sound before
 * the player has interacted).
 */
class SoundEngine {
  readonly enabled = createStore<boolean>(readJson('tpubg:sound', true));
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private lastPlayed = new Map<SoundName, number>();

  constructor() {
    this.enabled.subscribe(() => writeJson('tpubg:sound', this.enabled.get()));
  }

  toggle(): void {
    this.enabled.set(!this.enabled.get());
    if (this.enabled.get()) {
      this.unlock();
      this.play('click');
    }
  }

  /** Call from a user gesture handler. */
  unlock(): void {
    if (!this.enabled.get()) return;
    try {
      if (!this.ctx) {
        const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctor) return;
        this.ctx = new Ctor();
        this.master = this.ctx.createGain();
        this.master.gain.value = 0.5;
        this.master.connect(this.ctx.destination);
        const len = this.ctx.sampleRate * 0.6;
        this.noise = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
        const data = this.noise.getChannelData(0);
        for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
      }
      if (this.ctx.state === 'suspended') void this.ctx.resume();
    } catch {
      this.ctx = null;
    }
  }

  play(name: SoundName): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || !this.enabled.get() || ctx.state !== 'running') return;
    const t = ctx.currentTime;
    const minGap = name === 'bounce' || name === 'fizzle' ? 0.035 : 0.01;
    if (t - (this.lastPlayed.get(name) ?? -1) < minGap) return;
    this.lastPlayed.set(name, t);
    switch (name) {
      case 'fire0':
        return this.tone('square', 620, 200, 0.09, 0.11);
      case 'fire1':
        return this.tone('square', 520, 170, 0.09, 0.11);
      case 'bounce':
        return this.tone('triangle', 1300, 900, 0.045, 0.06);
      case 'fizzle':
        return this.noiseBurst(0.06, 2400, 1200, 0.05);
      case 'explode':
        this.noiseBurst(0.55, 1400, 120, 0.42);
        return this.tone('sine', 140, 38, 0.4, 0.4);
      case 'tick':
        return this.tone('sine', 660, 660, 0.09, 0.16);
      case 'go':
        return this.tone('sine', 990, 990, 0.2, 0.18);
      case 'round':
        this.tone('triangle', 523, 523, 0.12, 0.13);
        return this.tone('triangle', 784, 784, 0.18, 0.13, 0.12);
      case 'match':
        [523, 659, 784, 1047].forEach((f, i) => this.tone('triangle', f, f, 0.22, 0.14, i * 0.11));
        return;
      case 'click':
        return this.tone('sine', 880, 880, 0.05, 0.08);
    }
  }

  private tone(type: OscillatorType, from: number, to: number, dur: number, vol: number, delay = 0): void {
    const ctx = this.ctx!;
    const t = ctx.currentTime + delay;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(from, t);
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, to), t + dur);
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(vol, t + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(gain).connect(this.master!);
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }

  private noiseBurst(dur: number, fromHz: number, toHz: number, vol: number): void {
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(fromHz, t);
    filter.frequency.exponentialRampToValueAtTime(toHz, t + dur);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(vol, t);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(filter).connect(gain).connect(this.master!);
    src.start(t);
    src.stop(t + dur);
  }
}

export const sound = new SoundEngine();

export function useSoundEnabled(): boolean {
  return useStore(sound.enabled);
}
